import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Save, Mail, Phone, MapPin, Calendar as CalIcon, Pencil } from "lucide-react";
import DocumentsPanel from "@/components/DocumentsPanel";
import AvatarUploader from "@/components/AvatarUploader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function EmployeeDetail() {
  const { id } = useParams();
  const { user: current } = useAuth();
  const [u, setU] = useState(null);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({});
  const [vacations, setVacations] = useState([]);
  const [saving, setSaving] = useState(false);

  const isAdmin = current.role === "admin";
  const isSelf = current.id === id;

  const load = async () => {
    const [uRes, sRes] = await Promise.all([
      api.get(`/users/${id}`),
      api.get(`/vacations/stats/${id}`),
    ]);
    setU(uRes.data);
    setStats(sRes.data);
    setForm({
      name: uRes.data.name || "",
      position: uRes.data.position || "",
      department: uRes.data.department || "",
      phone: uRes.data.phone || "",
      address: uRes.data.address || "",
      birth_date: uRes.data.birth_date || "",
      hire_date: uRes.data.hire_date || "",
      vacation_days_per_year: uRes.data.vacation_days_per_year || 30,
      target_hours_per_week: uRes.data.target_hours_per_week ?? 40,
      role: uRes.data.role,
      password: "",
    });
    if (isAdmin) {
      const all = await api.get("/vacations", { params: { scope: "all" } });
      setVacations(all.data.filter((v) => v.user_id === id));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!u) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (!isAdmin) { delete payload.role; delete payload.vacation_days_per_year; }
      await api.put(`/users/${id}`, payload);
      toast.success("Gespeichert");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6">
      <Link to="/mitarbeiter" className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Zurück zur Übersicht
      </Link>

      <div className="ph-card p-6 flex flex-col md:flex-row md:items-center gap-6">
        <AvatarUploader user={u} editable={isAdmin || isSelf} size="xl" onChange={() => load()} />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display text-3xl font-semibold tracking-tight">{u.name}</h1>
            <Badge className={u.role === "admin" ? "bg-sky-700" : "bg-slate-200 text-slate-700 hover:bg-slate-200"}>
              {u.role === "admin" ? "Chef / Admin" : "Mitarbeiter"}
            </Badge>
          </div>
          <div className="text-slate-500 mt-1">{u.position} {u.department && `· ${u.department}`}</div>
          <div className="flex gap-4 mt-3 text-xs text-slate-500 flex-wrap">
            <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {u.email}</span>
            {u.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {u.phone}</span>}
            {u.hire_date && <span className="inline-flex items-center gap-1"><CalIcon className="h-3 w-3" /> Eingestellt {u.hire_date}</span>}
          </div>
        </div>
        {stats && (
          <div className="flex items-center gap-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <KpiMini label="Resturlaub" value={stats.remaining_days} />
              <KpiMini label="Genommen" value={stats.taken_days} />
              <KpiMini label="Offen" value={stats.pending_requests} />
            </div>
            {isAdmin && (
              <SetRemainingDialog userId={id} userName={u.name} stats={stats} onSaved={load} />
            )}
          </div>
        )}
      </div>

      <Tabs defaultValue="stammdaten">
        <TabsList>
          <TabsTrigger value="stammdaten" data-testid="tab-stammdaten">Stammdaten</TabsTrigger>
          <TabsTrigger value="dokumente" data-testid="tab-dokumente">Dokumente</TabsTrigger>
          {isAdmin && <TabsTrigger value="urlaube" data-testid="tab-urlaube">Urlaube</TabsTrigger>}
        </TabsList>

        <TabsContent value="stammdaten" className="mt-4">
          <div className="ph-card p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Name"><Input value={form.name} onChange={(e) => upd("name", e.target.value)} /></Field>
              <Field label="Position"><Input value={form.position} onChange={(e) => upd("position", e.target.value)} /></Field>
              <Field label="Abteilung"><Input value={form.department} onChange={(e) => upd("department", e.target.value)} /></Field>
              <Field label="Telefon"><Input value={form.phone} onChange={(e) => upd("phone", e.target.value)} /></Field>
              <Field label="Adresse"><Input value={form.address} onChange={(e) => upd("address", e.target.value)} /></Field>
              <Field label="Geburtsdatum"><Input type="date" value={form.birth_date} onChange={(e) => upd("birth_date", e.target.value)} /></Field>
              <Field label="Einstellungsdatum"><Input type="date" value={form.hire_date} onChange={(e) => upd("hire_date", e.target.value)} disabled={!isAdmin} /></Field>
              {isAdmin && (
                <Field label="Urlaubstage pro Jahr">
                  <Input type="number" min={0} max={60} value={form.vacation_days_per_year} onChange={(e) => upd("vacation_days_per_year", Number(e.target.value))} />
                </Field>
              )}
              {isAdmin && (
                <Field label="Zielstunden pro Woche">
                  <Input data-testid="input-target-hours-week" type="number" min={0} max={80} step="0.5" value={form.target_hours_per_week} onChange={(e) => upd("target_hours_per_week", Number(e.target.value))} />
                </Field>
              )}
              {isAdmin && (
                <Field label="Neues Passwort (optional)">
                  <Input type="text" value={form.password} onChange={(e) => upd("password", e.target.value)} placeholder="leer = nicht ändern" />
                </Field>
              )}
            </div>
            <div className="flex justify-end">
              <Button data-testid="btn-save-employee" onClick={save} disabled={saving} className="bg-kauz-navy hover:bg-kauz-navy-dark">
                <Save className="h-4 w-4 mr-2" /> {saving ? "Speichern…" : "Speichern"}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="dokumente" className="mt-4">
          <DocumentsPanel userId={id} canUpload={isAdmin || isSelf} canDelete={isAdmin} />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="urlaube" className="mt-4">
            <div className="ph-card overflow-hidden">
              <ul className="divide-y divide-slate-100">
                {vacations.map((v) => (
                  <li key={v.id} className="px-5 py-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{v.start_date} → {v.end_date}</div>
                      <div className="text-xs text-slate-500">{v.days} Werktage · {v.reason || "kein Grund"}</div>
                    </div>
                    <StatusBadge status={v.status} />
                  </li>
                ))}
                {vacations.length === 0 && (
                  <li className="text-center text-sm text-slate-400 py-10">Keine Anträge.</li>
                )}
              </ul>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
function KpiMini({ label, value }) {
  return (
    <div>
      <div className="ph-kpi text-2xl text-slate-900">{value}</div>
      <div className="ph-label">{label}</div>
    </div>
  );
}
function StatusBadge({ status }) {
  const m = {
    pending: ["bg-amber-50 text-amber-800 border-amber-200", "Offen"],
    approved: ["bg-emerald-50 text-emerald-800 border-emerald-200", "Genehmigt"],
    rejected: ["bg-red-50 text-red-800 border-red-200", "Abgelehnt"],
  };
  const [cls, label] = m[status] || m.pending;
  return <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md border ${cls}`}>{label}</span>;
}

function SetRemainingDialog({ userId, userName, stats, onSaved }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(stats.remaining_days);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setValue(stats.remaining_days); }, [stats.remaining_days, open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/users/${userId}/vacation/set-remaining`, { remaining_days: Number(value) });
      toast.success("Resturlaub aktualisiert");
      setOpen(false);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-9" data-testid="btn-edit-remaining">
          <Pencil className="h-3.5 w-3.5 mr-1.5" /> Resturlaub
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-display">Resturlaub bearbeiten</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-slate-600">
            Setze den Resturlaub für <strong>{userName}</strong> direkt. Die Differenz wird automatisch als
            Korrektur ({stats.adjustment_days >= 0 ? "+" : ""}{stats.adjustment_days} Tage aktuell) gebucht.
          </p>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase font-semibold text-slate-600">Resturlaub in Tagen</Label>
            <Input data-testid="set-remaining-input" type="number" min={0} max={120} required
              value={value} onChange={(e) => setValue(e.target.value)} className="text-2xl font-display h-14" />
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600 space-y-1">
            <div className="flex justify-between"><span>Jahresanspruch</span><span className="font-semibold tabular-nums">{stats.base_days} Tage</span></div>
            <div className="flex justify-between"><span>Bereits genommen</span><span className="font-semibold tabular-nums">{stats.taken_days} Tage</span></div>
            {stats.company_days > 0 && (
              <div className="flex justify-between"><span>Betriebsferien</span><span className="font-semibold tabular-nums">{stats.company_days} Tage</span></div>
            )}
            <div className="flex justify-between border-t border-slate-200 pt-1 mt-1"><span>Aktuelle Korrektur</span><span className="font-semibold tabular-nums">{stats.adjustment_days >= 0 ? "+" : ""}{stats.adjustment_days} Tage</span></div>
          </div>
          <DialogFooter>
            <Button type="submit" data-testid="set-remaining-submit" disabled={busy} className="bg-kauz-navy hover:bg-kauz-navy-dark">
              {busy ? "Speichern…" : "Speichern"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
